// src/components/products/ProductShowcase.tsx

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import type { RecommendProduct } from '../../services/api';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import { useImageWithFallback, getProductGradient } from '../../utils/imageLoader';
import { getProductImage } from '../../utils/getProductImage';
import { RecommendationInfo } from './RecommendationInfo';

const ShowcaseContainer = styled.div`
  margin: 20px 0;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
`;

const EmblaViewport = styled.div`
  overflow: hidden;
  cursor: grab;
  border-radius: 20px;

  &:active {
    cursor: grabbing;
  }
`;

const EmblaContainer = styled.div`
  display: flex;
  gap: 16px;
  touch-action: pan-y pinch-zoom;
  user-select: none;
  -webkit-user-drag: none;

  /* ✅ FIX: Уменьшаем gap на узких экранах для 360px */
  @media (max-width: 375px) {
    gap: 12px;
  }
`;

// ✅ Адаптивные размеры - ВЕРТИКАЛЬНЫЙ LAYOUT для мобильных
const ProductCard = styled(motion.div)<{ $expanded: boolean }>`
  flex: 0 0 auto;
  width: 100%;
  max-width: 280px;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.98),
    rgba(255, 255, 255, 0.95)
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(123, 104, 238, 0.2);
  border-radius: 24px;
  overflow: hidden;
  box-shadow:
    0 10px 40px rgba(123, 104, 238, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB, #9966CC);
    background-size: 200% 100%;
    animation: shimmer 3s ease infinite;
  }

  @keyframes shimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 50px rgba(123, 104, 238, 0.25);
  }

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.98),
      rgba(32, 32, 50, 0.95)
    );
    border: 1px solid rgba(196, 181, 253, 0.2);
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    &::before {
      background: linear-gradient(90deg, #C4B5FD, #A0D8F0, #D8B4FE);
    }

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
    }
  }

  /* ✅ FIX: Адаптация для мобильных - уменьшаем max-width */
  @media (max-width: 375px) {
    max-width: 260px;
  }
`;

// ✅ FIX: ВЕРТИКАЛЬНЫЙ LAYOUT - фото сверху, текст снизу
const ProductHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 16px;

  /* ✅ FIX: Уменьшаем padding на узких экранах */
  @media (max-width: 375px) {
    padding: 16px;
    gap: 12px;
  }
`;

const ProductImage = styled.div<{ $url: string }>`
  width: 100%;
  aspect-ratio: 1;
  max-width: 240px;
  margin: 0 auto;
  border-radius: 16px;
  background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
  background-image: url(${props => props.$url});
  background-size: cover;
  background-position: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg,
      transparent 0%,
      rgba(123, 104, 238, 0.1) 100%
    );
  }

  @media (max-width: 375px) {
    max-width: 220px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: center;
`;

const ProductName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  margin: 0 0 12px 0;
  line-height: 1.3;

  [data-theme="dark"] & {
    color: #ffffff;
  }

  @media (max-width: 375px) {
    font-size: 17px;
  }
`;

const ExpandButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  color: #7B68EE;
  font-size: 13px;
  font-weight: 600;

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  transition: transform 0.3s ease;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0)'};
`;

const ProductDetails = styled(motion.div)`
  padding: 0 20px 20px;
  overflow: hidden;
  transform-origin: top;
`;

const DetailSection = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #7B68EE;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const DetailText = styled.p`
  font-size: 14px;
  color: rgba(0,0,0,0.75);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const BenefitsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(0,0,0,0.75);
  line-height: 1.4;

  &::before {
    content: '✓';
    color: #22C55E;
    font-weight: 700;
    flex-shrink: 0;
  }

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const ActionButton = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white;
  text-decoration: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(123, 104, 238, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(123, 104, 238, 0.4);
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.85)
  );
  border: 1px dashed rgba(123, 104, 238, 0.3);
  border-radius: 20px;

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(42, 42, 60, 0.95),
      rgba(32, 32, 50, 0.85)
    );
    border: 1px dashed rgba(196, 181, 253, 0.3);
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: 15px;
  color: rgba(0,0,0,0.6);
  margin: 0;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
  }
`;

interface ProductShowcaseProps {
  products: RecommendProduct[];
  currentStage: 1 | 2 | 3;
}

// Компонент с fallback для изображений
const ProductImageWithFallback: React.FC<{ url: string; productId: string }> = ({ url, productId }) => {
  const { src, hasError } = useImageWithFallback(url);

  if (hasError || !src) {
    return (
      <ProductImage
        $url=""
        style={{
          backgroundImage: getProductGradient(productId),
        }}
      />
    );
  }

  return <ProductImage $url={src} />;
};

export const ProductShowcase: React.FC<ProductShowcaseProps> = ({ products, currentStage }) => {
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const { webApp, hapticFeedback } = useTelegramWebApp();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    skipSnaps: false,
    dragFree: true,
  });

  const toggleProduct = (productId: string) => {
    hapticFeedback('light');
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleProductClick = (url: string) => {
    hapticFeedback('success');
    if (webApp && typeof (webApp as any).openLink === 'function') {
      (webApp as any).openLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const parseDescription = (description: string) => {
    if (!description || typeof description !== 'string') {
      return { mainText: '', benefits: [] as string[] };
    }

    const lines = description.split('\n').filter(line => line.trim());
    const benefits = lines.filter(line => line.trim().startsWith('•') || line.trim().startsWith('-'));
    const mainText = lines
      .filter(line => !line.trim().startsWith('•') && !line.trim().startsWith('-'))
      .join('\n');

    return {
      mainText,
      benefits: benefits.map(b => b.replace(/^[•-]\s*/, ''))
    };
  };

  // ✅ FIX: getStageLabel удалена - плашка этапа больше не используется

  if (!products || products.length === 0) {
    return (
      <ShowcaseContainer>
        <EmptyState>
          <EmptyIcon>📦</EmptyIcon>
          <EmptyText>
            Рекомендуемые продукты для вашего этапа скоро появятся
          </EmptyText>
        </EmptyState>
      </ShowcaseContainer>
    );
  }

  return (
    <ShowcaseContainer>
      <RecommendationInfo products={products} />

      {/* ✅ Embla Carousel */}
      <EmblaViewport ref={emblaRef}>
        <EmblaContainer>
          {products.map((product, index) => {
            const isExpanded = expandedProducts.has(product.product_id);
            const { mainText, benefits } = parseDescription(product.description);

            return (
              <ProductCard
                key={product.product_id}
                $expanded={isExpanded}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <ProductHeader onClick={() => toggleProduct(product.product_id)}>
                  <ProductImageWithFallback
                    url={getProductImage(product) || ''}
                    productId={product.product_id}
                  />
                  <ProductInfo>
                    {/* ✅ FIX: Удалена плашка этапа и дубль описания */}
                    <ProductName>{product.product_name}</ProductName>
                    <ExpandButton>
                      <span>{isExpanded ? 'Скрыть' : 'Подробнее'}</span>
                      <ExpandIcon $expanded={isExpanded}>▼</ExpandIcon>
                    </ExpandButton>
                  </ProductInfo>
                </ProductHeader>

                <AnimatePresence initial={false} mode="wait">
                  {isExpanded && (
                    <ProductDetails
                      initial={{ scaleY: 0, opacity: 0 }}
                      animate={{ scaleY: 1, opacity: 1 }}
                      exit={{ scaleY: 0, opacity: 0 }}
                      transition={{
                        duration: 0.25,
                        ease: [0.4, 0, 0.2, 1],
                        opacity: { duration: 0.15 }
                      }}
                    >
                      {mainText && (
                        <DetailSection>
                          <DetailTitle>Описание</DetailTitle>
                          <DetailText>{mainText}</DetailText>
                        </DetailSection>
                      )}

                      {benefits.length > 0 && (
                        <DetailSection>
                          <DetailTitle>Преимущества</DetailTitle>
                          <BenefitsList>
                            {benefits.map((benefit, i) => (
                              <BenefitItem key={i}>{benefit}</BenefitItem>
                            ))}
                          </BenefitsList>
                        </DetailSection>
                      )}

                      <ActionButton
                        href={product.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product.product_url);
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span>Подробнее о продукте</span>
                        <span>→</span>
                      </ActionButton>
                    </ProductDetails>
                  )}
                </AnimatePresence>
              </ProductCard>
            );
          })}
        </EmblaContainer>
      </EmblaViewport>
    </ShowcaseContainer>
  );
};
